import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { CltCard } from '@/components/CalendarLogicTemplates/CltCard'
import { CltWizard } from '@/components/CalendarLogicTemplates/CltWizard'
import { useCltWizardStore } from '@/stores/useCltWizardStore'
import { defaultGroupsToStrategyGroups } from '@/stores/useCltWizardStore'
import {
  useDeleteTemplate,
  useSetDefaultTemplate,
  useTemplateSummaries,
} from '@/hooks/useCltTemplates'
import { useDefaultStrategyGroups } from '@/hooks/useCltReference'
import { toast } from '@/stores/useToastStore'
import { getTemplate } from '@/api/clt'
import { fromApiTemplate } from '@/api/clt.adapter'
import type { UiCalendarLogicTemplateSummary } from '@/api/clt.adapter'
import { getErrorMessage } from '@/utils/getErrorMessage'

export default function AdminCalendarLogicTemplates() {
  const templatesQuery = useTemplateSummaries()
  const deleteMutation = useDeleteTemplate()
  const setDefaultMutation = useSetDefaultTemplate()
  const { data: defaultGroups = [] } = useDefaultStrategyGroups()
  const queryClient = useQueryClient()

  const openForCreate = useCltWizardStore(s => s.openForCreate)
  const openForEdit = useCltWizardStore(s => s.openForEdit)
  const closeWizard = useCltWizardStore(s => s.close)

  useEffect(() => {
    return () => closeWizard()
  }, [closeWizard])

  const handleEdit = async (template: UiCalendarLogicTemplateSummary) => {
    try {
      const full = await queryClient.fetchQuery({
        queryKey: ['clt', template.id],
        queryFn: async () => fromApiTemplate(await getTemplate(template.id)),
      })
      openForEdit(full)
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to load template'))
    }
  }

  const [search, setSearch] = useState('')
  const [deleteTarget, setDeleteTarget] =
    useState<UiCalendarLogicTemplateSummary | null>(null)

  const templates = useMemo(
    () => templatesQuery.data ?? [],
    [templatesQuery.data]
  )

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return templates
    return templates.filter(
      t =>
        t.name.toLowerCase().includes(query) ||
        (t.description ?? '').toLowerCase().includes(query)
    )
  }, [templates, search])

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    const target = deleteTarget
    deleteMutation.mutate(target.id, {
      onSuccess: () => {
        toast.success(`Deleted "${target.name}"`)
      },
    })
    setDeleteTarget(null)
  }

  const handleSetDefault = (template: UiCalendarLogicTemplateSummary) => {
    setDefaultMutation.mutate(template.id, {
      onSuccess: () => {
        toast.success(`"${template.name}" is now the default template`)
      },
    })
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Calendar Logic Templates
          </h1>
          <p className="mt-1 text-base text-muted-foreground">
            Define how milestone dates are calculated and where each template
            applies.
          </p>
        </div>
        <Button
          className="cursor-pointer"
          onClick={() =>
            openForCreate(defaultGroupsToStrategyGroups(defaultGroups))
          }
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Template
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Templates List */}
      {templatesQuery.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Loading templates…
        </div>
      ) : templatesQuery.isError ? (
        <div className="py-12 text-center text-sm text-destructive">
          Failed to load templates.
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-lg font-medium text-muted-foreground">
            No templates found
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {search
              ? 'Try adjusting your search.'
              : 'Create your first Calendar Logic Template to get started.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(template => (
            <CltCard
              key={template.id}
              template={template}
              onClick={() => handleEdit(template)}
              onDelete={() => setDeleteTarget(template)}
              onSetDefault={() => handleSetDefault(template)}
            />
          ))}
        </div>
      )}

      {/* Wizard overlay */}
      <CltWizard />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open) setDeleteTarget(null)
        }}
        title="Delete Template"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
